import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Container,
  Alert,
} from "@mui/material";
import {
  FaceRetouchingNatural,
  Share,
  Check,
  Refresh,
  Language,
} from "@mui/icons-material";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { useParams, useNavigate } from "react-router-dom";
import { api, ImageFile, StyleResponse } from "./request";
import ImageDetailDialog from "./ImageDetailDialog";
import { printPhoto } from "./printPhoto";

// 与首页一致的全局主题
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#2196f3",
    },
    secondary: {
      main: "#f44336",
    },
    background: {
      default: "#f5f7fa",
    },
  },
  typography: {
    fontFamily:
      '"IBM Plex Sans TC", "Noto Sans TC", -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang TC", "Microsoft JhengHei", "Helvetica Neue", Arial, sans-serif',
    h1: { fontFamily: '"Noto Serif TC", "Noto Serif", "Songti TC", Georgia, serif', fontWeight: 600 },
    h2: { fontFamily: '"Noto Serif TC", "Noto Serif", "Songti TC", Georgia, serif', fontWeight: 600 },
    h3: { fontFamily: '"Noto Serif TC", "Noto Serif", "Songti TC", Georgia, serif', fontWeight: 600 },
    h4: { fontFamily: '"Noto Serif TC", "Noto Serif", "Songti TC", Georgia, serif', fontWeight: 600 },
    h5: { fontFamily: '"Noto Serif TC", "Noto Serif", "Songti TC", Georgia, serif', fontWeight: 600 },
    h6: { fontFamily: '"Noto Serif TC", "Noto Serif", "Songti TC", Georgia, serif', fontWeight: 600 },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 10,
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          boxShadow: "0 1px 4px rgba(15, 40, 80, 0.07)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
  },
});

// ─── 多语言（与首页共用 gallery_lang 持久化） ───────────
type Lang = "zh-TW" | "en";

const I18N = {
  "zh-TW": {
    verifyingLink: "正在驗證連結...",
    linkInvalid: "鏈接無效",
    verifyLinkFailed: "無法驗證鏈接：{message}",
    backHome: "返回首頁",
    loadedSharedResults: "已載入分享的 {count} 張相片",
    copiedShareLink: "已複製分享鏈接",
    fileTooLarge: "圖片大小不能超過10MB",
    compressing: "正在壓縮圖片...",
    searching: "正在搜索匹配的圖片...",
    searchingText: "正在搜索...",
    noMatch: "未找到匹配的圖片",
    searchFailed: "人臉搜索失敗",
    searchFailedMsg: "人臉搜索失敗：{message}",
    foundCount: "找到 {count} 張相片",
    share: "分享",
    rescan: "重新掃臉",
    uploadTitle: "自拍一張即可搜尋您的相片",
    uploadHint: "點擊此處上傳照片",
    consentTitle: "以人臉尋找你的相片",
    consentBody: "我們會使用生物辨識技術分析你的臉部，以便在活動相片中幫您快速找出精彩瞬間。（溫馨提示：此功能為自願性質。按下同意並繼續鍵代表您同意系統使用及分析您的面部數據。）",
    cancel: "取消",
    agreeAndContinue: "同意並繼續",
    learnMore: "了解更多",
    learnMoreTitle: "免責與隱私聲明",
    learnMoreBody: "我們會使用生物辨識技術分析你的臉部特徵，以在活動相片中自動搜尋並提供包含你的照片。【免責與隱私聲明】：您參與本活動即代表同意主辦單位處理上述個人生物特徵資料。我們承諾嚴格遵守個人資料隱私條例，相關數據將不會儲存於系統內，且絕不向第三方透露。",
    shareTitle: "分享本次搜尋結果",
    shareBody: "掃描下方二維碼，即可查看本次找到的 {count} 張相片",
    shareFail: "無法生成分享鏈接",
    copyLink: "複製鏈接",
    close: "關閉",
    download: "下載",
    print: "列印",
    printTitle: "列印 - {name}",
    delete: "刪除",
    deleting: "正在刪除...",
    confirmDelete: "確定刪除 {name} 嗎？",
    deleted: "已刪除 {name}",
    deleteFailed: "刪除失敗",
    deleteFailedWithError: "刪除失敗：{error}",
    scanDownload: "掃碼下載",
    qrAlt: "下載二維碼",
    sizeLabel: "大小：",
    modifiedLabel: "修改時間：",
    wantMorePerson: "想看更多這個人的相片嗎？",
    viewPerson: "查看此人",
  },
  en: {
    verifyingLink: "Verifying link...",
    linkInvalid: "Invalid link",
    verifyLinkFailed: "Failed to verify link: {message}",
    backHome: "Back to Home",
    loadedSharedResults: "Loaded {count} shared photos",
    copiedShareLink: "Share link copied",
    fileTooLarge: "Image size cannot exceed 10MB",
    compressing: "Compressing image...",
    searching: "Searching for matching photos...",
    searchingText: "Searching...",
    noMatch: "No matching photos found",
    searchFailed: "Face search failed",
    searchFailedMsg: "Face search failed: {message}",
    foundCount: "Found {count} photos",
    share: "Share",
    rescan: "Scan Again",
    uploadTitle: "Take a selfie to find your photos",
    uploadHint: "Click here to upload a photo",
    consentTitle: "Find your photos with your face",
    consentBody: "We will use biometric technology to analyze your face in order to help you quickly find memorable moments in event photos. (Tip: This feature is entirely voluntary. Clicking 'Agree & Continue' indicates your consent to the system's use and analysis of your facial data.)",
    cancel: "Cancel",
    agreeAndContinue: "Agree & Continue",
    learnMore: "Learn More",
    learnMoreTitle: "Disclaimer & Privacy Statement",
    learnMoreBody: "We will use biometric technology to analyze your face to locate you in event photos. [Disclaimer & Privacy Statement] By participating in this event, you consent to the organizer processing the aforementioned personal biometric data. The organizer undertakes to strictly comply with personal data privacy regulations, and the relevant recognition data will not be stored in the system. In the event of data leakage risks caused by force majeure or malicious cyberattacks, the organizer will take remedial measures in accordance with the law, but shall not assume any liability for compensation beyond what is prescribed by law.",
    shareTitle: "Share These Results",
    shareBody: "Scan the QR code below to view all {count} photos found",
    shareFail: "Unable to generate share link",
    copyLink: "Copy Link",
    close: "Close",
    download: "Download",
    print: "Print",
    printTitle: "Print - {name}",
    delete: "Delete",
    deleting: "Deleting...",
    confirmDelete: "Delete {name}?",
    deleted: "Deleted {name}",
    deleteFailed: "Delete failed",
    deleteFailedWithError: "Delete failed: {error}",
    scanDownload: "Scan to download",
    qrAlt: "QR code for download",
    sizeLabel: "Size: ",
    modifiedLabel: "Modified: ",
    wantMorePerson: "Want to see more photos of this person?",
    viewPerson: "View This Person",
  },
} as const;

type I18nKey = keyof typeof I18N.en;

// ── 样式配置缓存：避免 header 高度在接口返回前跳动 ──────────
type StyleConfig = {
  headerHeightXs: number;
  headerHeightMd: number;
  headerWidthXs: number;
  headerWidthMd: number;
  logoHeight: number;
  headerOverlayOpacity: number;
  backgroundOverlayOpacity: number;
};

const DEFAULT_STYLE_CONFIG: StyleConfig = {
  headerHeightXs: 140,
  headerHeightMd: 220,
  headerWidthXs: 750,
  headerWidthMd: 1920,
  logoHeight: 36,
  headerOverlayOpacity: 0.35,
  backgroundOverlayOpacity: 0,
};

type HeaderCache = {
  header_url: string | null;
  header_url_xs: string | null;
  header_url_md: string | null;
};

const readCachedJson = <T,>(key: string): T | null => {
  if (typeof window === "undefined" || !key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const writeCachedJson = (key: string, value: unknown) => {
  if (typeof window === "undefined" || !key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // 缓存失败静默忽略
  }
};

const getInitialLang = (): Lang => {
  if (typeof window === "undefined") {
    return "zh-TW";
  }
  const saved = window.localStorage.getItem("gallery_lang");
  if (saved === "zh-TW" || saved === "en") {
    return saved;
  }
  const browserLang = (navigator.language || "").toLowerCase();
  if (browserLang.startsWith("zh")) {
    return "zh-TW";
  }
  return "en";
};

const translate = (lang: Lang, key: I18nKey, vars?: Record<string, string | number>) => {
  const template = I18N[lang][key] || I18N.en[key] || key;
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, token) => String(vars[token] ?? ""));
};

// ─── 页面组件 ────────────────────────────────────────────
function FaceSearchPage() {
  const { link } = useParams<{ link: string }>();
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

  // ── 语言（与首页共用 gallery_lang 持久化） ────────────
  const initialLang = useMemo(() => getInitialLang(), []);
  const [lang, setLang] = useState<Lang>(initialLang);
  const t = (key: I18nKey, vars?: Record<string, string | number>) =>
    translate(lang, key, vars);

  // ── 语言切换持久化（与首页共用 gallery_lang） ──────────
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang === "zh-TW" ? "zh-Hant" : "en";
    }
    if (typeof window !== "undefined") {
      window.localStorage.setItem("gallery_lang", lang);
    }
  }, [lang]);

  // ── 状态 ──────────────────────────────────────────────
  const [consentOpen, setConsentOpen] = useState(false);
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<ImageFile[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // 横幅样式数据（与首页 MainApp 完全一致）
  const [headerImageUrl, setHeaderImageUrl] = useState<string | null>(
    () => readCachedJson<HeaderCache>(link ? `gallery_header_${link}` : "")?.header_url || null
  );
  const [headerImageUrlXs, setHeaderImageUrlXs] = useState<string | null>(
    () => readCachedJson<HeaderCache>(link ? `gallery_header_${link}` : "")?.header_url_xs || null
  );
  const [headerImageUrlMd, setHeaderImageUrlMd] = useState<string | null>(
    () => readCachedJson<HeaderCache>(link ? `gallery_header_${link}` : "")?.header_url_md || null
  );
  const [logoImageUrl, setLogoImageUrl] = useState<string | null>(null);
  const [logoSize, setLogoSize] = useState<number | null>(null);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState<string | null>(null);
  const [styleConfig, setStyleConfig] = useState<StyleConfig>(() => ({
    ...DEFAULT_STYLE_CONFIG,
    ...(readCachedJson<Partial<StyleConfig>>(link ? `gallery_style_${link}` : "") || {}),
  }));
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0
  );
  const [linkValid, setLinkValid] = useState<boolean | null>(null);
  const [linkChecking, setLinkChecking] = useState(true);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── 统计数据（从后端加载真实数据） ─────────────────────
  const [stats, setStats] = useState({ peopleCount: 0, photoCount: 0 });

  // ── 监听窗口大小 ──────────────────────────────────────
  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── 验证 link 并加载样式（与首页 MainApp 一致） ─────────
  useEffect(() => {
    if (!link) return;
    let cancelled = false;

    const verifyAndLoad = async (retryCount = 0) => {
      if (cancelled) return;
      try {
        const data = await api.verifyLink(link);
        if (cancelled) return;
        if (data.success && data.face_enabled === false) {
          navigate(`/gallery/${link}`, { replace: true });
          return;
        }
        setLinkValid(data.success);
        if (data.success) {
          loadStats();
        } else {
          setError(data.message || t("linkInvalid"));
        }
      } catch (err) {
        if (cancelled) return;
        // 自动重试一次（防止网络抖动）
        if (retryCount < 1) {
          setTimeout(() => verifyAndLoad(retryCount + 1), 500);
          return;
        }
        setLinkValid(false);
        setError(t("verifyLinkFailed", { message: String(err) }));
      }
    };

    // 添加小延迟，确保组件完全挂载
    const timer = setTimeout(() => {
      verifyAndLoad();
      loadStyle();
      loadHeaderImage();
      loadLogoImage();
      loadBackgroundImage();
    }, 100);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link]);

  // ── 从分享链接恢复搜索结果（?data=id:bbox|id:bbox，兼容旧 ?ids=） ──
  useEffect(() => {
    if (!link) return;
    const params = new URLSearchParams(window.location.search);
    const dataParam = params.get("data") || params.get("ids");
    if (!dataParam) return;
    let cancelled = false;
    api.faceResults(link, dataParam)
      .then((data) => {
        if (cancelled) return;
        if (data.success && Array.isArray(data.images) && data.images.length > 0) {
          setResults(data.images);
          setStatus(t("loadedSharedResults", { count: data.images.length }));
        }
      })
      .catch(() => {
        // 静默失败，不影响页面
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link]);

  const loadHeaderImage = async () => {
    if (!link) return;
    try {
      const data = await api.getHeaderImage(link);
      if (data.success) {
        setHeaderImageUrl(data.header_url || null);
        setHeaderImageUrlXs(data.header_url_xs || null);
        setHeaderImageUrlMd(data.header_url_md || null);
        writeCachedJson(`gallery_header_${link}`, {
          header_url: data.header_url || null,
          header_url_xs: data.header_url_xs || null,
          header_url_md: data.header_url_md || null,
        });
      }
    } catch (error) {
      // 静默失败，不影响主功能
      console.error("Failed to load header image:", error);
    }
  };

  // 从后端加载真实的人脸搜索统计数据（搜索人数、累计找到的照片数）
  const loadStats = async () => {
    if (!link) return;
    try {
      const data = await api.faceSearchStats(link);
      if (data.success) {
        setStats({
          peopleCount: data.peopleCount ?? 0,
          photoCount: data.photoCount ?? 0,
        });
      }
    } catch (error) {
      // 静默失败，不影响主功能
      console.error("Failed to load face search stats:", error);
    }
  };

  const loadBackgroundImage = async () => {
    if (!link) return;
    try {
      const data = await api.getBackgroundImage(link);
      if (data.success && data.background_url) {
        setBackgroundImageUrl(data.background_url);
      }
    } catch (error) {
      // 静默失败，不影响主功能
      console.error("Failed to load background image:", error);
    }
  };

  const loadLogoImage = async () => {
    if (!link) return;
    try {
      const data = await api.getLogoImage(link);
      if (data.success && data.logo_url) {
        setLogoImageUrl(data.logo_url);
        setLogoSize(data.logo_size ?? null);
      } else {
        setLogoImageUrl(null);
        setLogoSize(data.logo_size ?? null);
      }
    } catch (error) {
      // 静默失败，不影响主功能
      console.error("Failed to load logo image:", error);
    }
  };

  const loadStyle = async () => {
    if (!link) return;
    try {
      const res: StyleResponse = await api.getStyle(link);
      if (res && res.success) {
        const next: StyleConfig = {
          headerHeightXs: Number(res.header_height_xs || 140),
          headerHeightMd: Number(res.header_height_md || 220),
          headerWidthXs: Number(res.header_width_xs || 750),
          headerWidthMd: Number(res.header_width_md || 1920),
          logoHeight: Number(res.logo_height || 36),
          headerOverlayOpacity: Number(res.header_overlay_opacity ?? 0.35),
          backgroundOverlayOpacity: Number(res.background_overlay_opacity ?? 0),
        };
        setStyleConfig(next);
        writeCachedJson(`gallery_style_${link}`, next);
      }
    } catch (e) {
      // keep defaults on failure
    }
  };

  // ── 图片压缩 ──────────────────────────────────────────
  const compressImage = (file: File, maxPixels: number = 1024): Promise<File> => {
    return new Promise((resolve, reject) => {
      if (file.size < 200 * 1024 && file.type === "image/jpeg") {
        resolve(file);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          let { width, height } = img;
          if (width > maxPixels || height > maxPixels) {
            if (width > height) {
              height = Math.round((height / width) * maxPixels);
              width = maxPixels;
            } else {
              width = Math.round((width / height) * maxPixels);
              height = maxPixels;
            }
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (!ctx) { resolve(file); return; }
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (!blob) { resolve(file); return; }
            const compressed = new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), {
              type: "image/jpeg",
            });
            resolve(compressed);
          }, "image/jpeg", 0.85);
        };
        img.onerror = () => resolve(file);
        img.src = reader.result as string;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  // ── 同意后上传 ────────────────────────────────────────
  const handleConsentAgree = () => {
    setConsentOpen(false);
    fileInputRef.current?.click();
  };

  const handleConsentCancel = () => {
    setConsentOpen(false);
  };

  // ── 点击上传区域 ──────────────────────────────────────
  const handleUploadClick = () => {
    setConsentOpen(true);
  };

  // ── 文件选择处理 ──────────────────────────────────────
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError(t("fileTooLarge"));
      e.target.value = "";
      return;
    }
    e.target.value = "";
    setSearching(true);
    setError("");
    setStatus(t("compressing"));
    try {
      const compressed = await compressImage(file, 720);
      performFaceSearch(compressed);
    } catch {
      performFaceSearch(file);
    }
  };

  const performFaceSearch = async (file: File) => {
    if (!link) return;
    setSearching(true);
    setError("");
    setStatus(t("searching"));
    setResults([]);
    try {
      const data = await api.faceSearch(link, file);
      if (data.success) {
        const images = data.images || [];
        setResults(images);
        if (images.length > 0) {
          // 显示结果列表，不直接打开查看器
          loadStats();
        } else {
          setError(t("noMatch"));
          loadStats();
        }
      } else {
        setError(data.message || t("searchFailed"));
      }
    } catch (err) {
      setError(t("searchFailedMsg", { message: String(err) }));
    } finally {
      setSearching(false);
      setStatus("");
    }
  };

  const handleOpenViewer = (index: number) => {
    setCurrentIndex(index);
    setDialogOpen(true);
  };

  const handleRescanFace = () => {
    setResults([]);
    setError("");
  };

  const handleShareSearch = () => {
    setShareDialogOpen(true);
  };

  const shareSearchUrl = (() => {
    if (!link || results.length === 0) return "";
    const pairs = results
      .map((img) => {
        const imgId = img.id ?? (img as any).key;
        if (imgId == null || imgId === "") return "";
        return `${imgId}:${img.faceBbox || ""}`;
      })
      .filter((v) => v !== "")
      .join("|");
    if (!pairs) return "";
    return `${window.location.origin}/face-search/${link}?data=${encodeURIComponent(pairs)}`;
  })();
  const shareQrUrl = shareSearchUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(shareSearchUrl)}`
    : "";

  const handleCopyShareUrl = () => {
    if (!shareSearchUrl) return;
    navigator.clipboard?.writeText(shareSearchUrl).then(() => {
      setStatus(t("copiedShareLink"));
    }).catch(() => {
      setStatus(shareSearchUrl);
    });
  };

  const currentImage = results[currentIndex];

  const getQRCodeUrl = (imageUrl: string) => {
    if (!imageUrl) return "";
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
      imageUrl
    )}`;
  };

  const handleDownload = () => {
    if (!currentImage?.url) return;
    window.open(currentImage.url, "_blank");
  };

  const handlePrint = () => {
    if (!currentImage) return;
    printPhoto(currentImage.url, t("printTitle", { name: currentImage.name }));
  };

  const handleDeleteImage = async () => {
    if (!link || !currentImage) return;
    if (!window.confirm(t("confirmDelete", { name: currentImage.name }))) {
      return;
    }
    setDeleting(true);
    try {
      const result = await api.deleteImage(link, currentImage.path);
      if (result.success) {
        setStatus(t("deleted", { name: currentImage.name }));
        const next = results.filter((_, i) => i !== currentIndex);
        setResults(next);
        setDialogOpen(false);
      } else {
        setError(result.message || t("deleteFailed"));
      }
    } catch (error) {
      setError(t("deleteFailedWithError", { error: String(error) }));
    } finally {
      setDeleting(false);
    }
  };

  const detailLabels = {
    sizeLabel: t("sizeLabel"),
    modifiedLabel: t("modifiedLabel"),
    scanDownload: t("scanDownload"),
    qrAlt: t("qrAlt"),
    download: t("download"),
    print: t("print"),
    delete: t("delete"),
    deleting: t("deleting"),
  };

  // ── 分辨率计算（与首页 MainApp 完全一致） ───────────────
  const resolvedHeaderImageUrl = isMobile
    ? headerImageUrlXs || headerImageUrlMd || headerImageUrl
    : headerImageUrlMd || headerImageUrl || headerImageUrlXs;

  const headerScaleXs = Math.min(
    1,
    Math.max(0.2, viewportWidth / (styleConfig.headerWidthXs || 750))
  );
  const headerScaleMd = Math.min(
    1,
    Math.max(0.2, viewportWidth / (styleConfig.headerWidthMd || 1920))
  );
  const effectiveHeaderHeightXs = Math.max(
    1,
    Math.round((styleConfig.headerHeightXs || 140) * headerScaleXs)
  );
  const effectiveHeaderHeightMd = Math.max(
    1,
    Math.round((styleConfig.headerHeightMd || 220) * headerScaleMd)
  );

  const effectiveLogoHeight = Math.min(
    styleConfig.logoHeight || 36,
    resolvedHeaderImageUrl ? 64 : 40
  );

  // ── 加载态 ────────────────────────────────────────────
  if (linkChecking && linkValid === null) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", bgcolor: "#f5f7fa" }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">{t("verifyingLink")}</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  if (linkValid === false) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", bgcolor: "#f5f7fa" }}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>{error || t("linkInvalid")}</Alert>
          <Button variant="outlined" onClick={() => navigate("/")}>{t("backHome")}</Button>
        </Box>
      </ThemeProvider>
    );
  }

  // ── 主页面布局（header 与首页 MainApp 完全一致） ────────
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          ...(backgroundImageUrl
            ? {
                backgroundImage: `url(${backgroundImageUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundAttachment: "fixed",
              }
            : {
                bgcolor: "#f5f7fa",
              }),
        }}
      >
        {/* 背景遮罩（与首页一致） */}
        {backgroundImageUrl && styleConfig.backgroundOverlayOpacity > 0 ? (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              bgcolor: `rgba(255,255,255,${Math.max(
                0,
                Math.min(0.95, styleConfig.backgroundOverlayOpacity)
              )})`,
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        ) : null}

        {/* 顶部状态栏区域白条：保持手机状态栏背景为白色 */}
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: "env(safe-area-inset-top, 24px)",
            bgcolor: "#ffffff",
            zIndex: 1250,
            pointerEvents: "none",
          }}
        />

        {/* ============ 顶部 Header（与首页 MainApp 完全一致） ============ */}
        <AppBar
          position="sticky"
          elevation={resolvedHeaderImageUrl ? 0 : 4}
          sx={{
            pt: "env(safe-area-inset-top, 24px)",
            ...(resolvedHeaderImageUrl
              ? {
                  backgroundImage: `url(${resolvedHeaderImageUrl})`,
                  // Stretch to fill the header frame even if the image is smaller.
                  backgroundSize: "100% 100%",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  minHeight: {
                    xs: effectiveHeaderHeightXs,
                    md: effectiveHeaderHeightMd,
                  },
                }
              : {
                  bgcolor: "primary.main",
                }),
          }}
        >
          <Toolbar
            sx={{
              position: "relative",
              minHeight: resolvedHeaderImageUrl
                ? {
                    xs: effectiveHeaderHeightXs,
                    md: effectiveHeaderHeightMd,
                  }
                : 80,
              alignItems: resolvedHeaderImageUrl ? "flex-end" : "center",
              bgcolor: resolvedHeaderImageUrl
                ? `rgba(0,0,0,${Math.max(
                    0,
                    Math.min(0.95, styleConfig.headerOverlayOpacity)
                  )})`
                : "transparent",
              backdropFilter: "none",
            }}
          >
            {logoImageUrl && (
              <Box
                component="img"
                src={logoImageUrl}
                alt="Logo"
                sx={{
                  position: "absolute",
                  left: { xs: 12, md: 16 },
                  top: resolvedHeaderImageUrl ? { xs: 8, md: 12 } : "50%",
                  transform: resolvedHeaderImageUrl ? "none" : "translateY(-50%)",
                  height: effectiveLogoHeight,
                  width: "auto",
                  maxWidth: { xs: 160, md: 240 },
                  objectFit: "contain",
                  zIndex: 1,
                }}
              />
            )}
            {/* 语言切换（与首页一致）：单图标点击循环切换 */}
            <IconButton
              onClick={() => setLang(lang === "zh-TW" ? "en" : "zh-TW")}
              size="small"
              aria-label="language"
              sx={{
                position: "absolute",
                right: { xs: 12, md: 16 },
                top: resolvedHeaderImageUrl ? { xs: 8, md: 12 } : "50%",
                transform: resolvedHeaderImageUrl ? "none" : "translateY(-50%)",
                color: resolvedHeaderImageUrl ? "white" : "text.secondary",
                border: resolvedHeaderImageUrl
                  ? "1px solid rgba(255,255,255,0.4)"
                  : "1px solid rgba(0,0,0,0.08)",
                bgcolor: resolvedHeaderImageUrl
                  ? "rgba(255,255,255,0.15)"
                  : "rgba(0,0,0,0.03)",
                "&:hover": {
                  bgcolor: resolvedHeaderImageUrl
                    ? "rgba(255,255,255,0.25)"
                    : "rgba(0,0,0,0.06)",
                },
                zIndex: 1,
              }}
            >
              <Language fontSize="small" />
            </IconButton>
            <Box sx={{ flexGrow: 1 }} />
          </Toolbar>
        </AppBar>

        {/* ============ 主内容区 ============ */}
        <Container maxWidth="sm" sx={{ pt: { xs: 2, sm: 3 }, pb: { xs: 2.5, sm: 4 }, px: { xs: 1.5, sm: 3 }, position: "relative", zIndex: 1 }}>
          {/* 隐藏的文件输入 */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          {/* 状态提示 */}
          {status && (
            <Alert severity="info" sx={{ mb: 2 }} onClose={() => setStatus("")}>
              {status}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {/* ============ 搜索结果列表 / 上传卡片 ============ */}
          {results.length > 0 ? (
            /* ── 搜索结果列表 ── */
            <Box>
              {/* 结果标题 + 分享 + 重新掃臉 */}
              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                mb: 2, px: 0.5, gap: 1, flexWrap: "wrap",
              }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#212121", fontSize: { xs: 16, sm: 20 } }}>
                  {t("foundCount", { count: results.length })}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Button
                    variant="contained"
                    size={isMobile ? "small" : "medium"}
                    startIcon={<Share />}
                    onClick={handleShareSearch}
                    disableElevation
                    sx={{
                      borderRadius: "24px", textTransform: "none",
                      bgcolor: "#1976d2", px: { xs: 2, sm: 2.5 }, py: 0.5,
                      "&:hover": { bgcolor: "#1565c0" },
                    }}
                  >
                    {t("share")}
                  </Button>
                  <Button
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                    startIcon={<Refresh />}
                    onClick={handleRescanFace}
                    sx={{
                      borderRadius: "24px", textTransform: "none",
                      borderColor: "#1976d2", color: "#1976d2",
                      px: { xs: 2, sm: 2.5 }, py: 0.5,
                      "&:hover": { bgcolor: "rgba(25,118,210,0.06)", borderColor: "#1565c0" },
                    }}
                  >
                    {t("rescan")}
                  </Button>
                </Box>
              </Box>

              {/* 照片网格 */}
              <Box sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
                gap: { xs: 1.5, sm: 2 },
              }}>
                {results.map((img, idx) => (
                  <Box key={idx}>
                    {/* 照片 */}
                    <Box
                      onClick={() => handleOpenViewer(idx)}
                      sx={{
                        position: "relative",
                        borderRadius: 2.5,
                        overflow: "hidden",
                        cursor: "pointer",
                        aspectRatio: "1 / 1",
                        bgcolor: "#e8ecf1",
                        mb: 1,
                        boxShadow: "0 1px 3px rgba(15,40,80,0.08)",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-3px)",
                          boxShadow: "0 6px 18px rgba(15,40,80,0.15)",
                        },
                        "&:active": { transform: "scale(0.97)" },
                      }}
                    >
                      <img
                        src={img.thumbnail_url || img.url || ""}
                        alt={img.name || `照片 ${idx + 1}`}
                        style={{
                          width: "100%", height: "100%",
                          objectFit: "cover",
                        }}
                        loading="lazy"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>

            </Box>
          ) : (
            /* ── 上传卡片（无结果时显示） ── */
            <Card sx={{
              borderRadius: 4,
              boxShadow: "0 2px 16px rgba(15,40,80,0.08)",
              border: "1px solid rgba(0,0,0,0.05)",
              cursor: searching ? "default" : "pointer",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              ...(!searching && {
                "&:hover": { transform: "translateY(-3px)", boxShadow: "0 8px 28px rgba(15,40,80,0.14)" },
              }),
            }}
            onClick={searching ? undefined : handleUploadClick}
            >
              <CardContent sx={{ textAlign: "center", py: { xs: 5, sm: 6 }, px: 3 }}>
                {searching ? (
                  /* 搜索中 */
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 2 }}>
                    <CircularProgress size={56} sx={{ mb: 2.5 }} />
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {status || t("searchingText")}
                    </Typography>
                  </Box>
                ) : (
                  /* 人脸搜索区域 */
                  <Box>
                    {/* 人脸图标 */}
                    <Box sx={{
                      width: 80, height: 80, borderRadius: "20px",
                      border: "2px solid #1976d2",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      mx: "auto", mb: 3,
                      bgcolor: "rgba(25,118,210,0.04)",
                    }}>
                      <FaceRetouchingNatural sx={{ fontSize: 40, color: "#1976d2" }} />
                    </Box>

                    {/* 主标题 */}
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: "#212121" }}>
                      {t("uploadTitle")}
                    </Typography>

                    {/* 副标题 */}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t("uploadHint")}
                    </Typography>

                  </Box>
                )}
              </CardContent>
            </Card>
          )}

        </Container>

        {/* ============ 同意弹窗 ============ */}
        <Dialog
          open={consentOpen}
          onClose={handleConsentCancel}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, bgcolor: "#ffffff" },
          }}
        >
          <DialogContent sx={{ textAlign: "center", py: { xs: 3.5, sm: 4 }, px: { xs: 2.5, sm: 3 } }}>
            {/* 生物识别图标 */}
            <Box sx={{
              width: 72, height: 72, borderRadius: "50%",
              border: "3px solid #1976d2",
              display: "flex", alignItems: "center", justifyContent: "center",
              mx: "auto", mb: 2.5,
              bgcolor: "rgba(25,118,210,0.06)",
            }}>
              <FaceRetouchingNatural sx={{ fontSize: 36, color: "#1976d2" }} />
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              {t("consentTitle")}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
              {t("consentBody")}
            </Typography>

            <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
              <Button
                onClick={handleConsentCancel}
                sx={{
                  borderRadius: "24px", textTransform: "none",
                  color: "#757575", bgcolor: "#f5f5f5",
                  px: 3, minWidth: 100,
                  "&:hover": { bgcolor: "#e0e0e0" },
                }}
              >
                {t("cancel")}
              </Button>
              <Button
                variant="contained"
                startIcon={<Check />}
                onClick={handleConsentAgree}
                sx={{
                  borderRadius: "24px", textTransform: "none",
                  bgcolor: "#1976d2", px: 3, minWidth: 140,
                  "&:hover": { bgcolor: "#1565c0" },
                }}
              >
                {t("agreeAndContinue")}
              </Button>
            </Box>

            {/* 了解更多链接 */}
            <Typography
              variant="caption"
              component="button"
              onClick={() => setLearnMoreOpen(true)}
              sx={{
                display: "block", mt: 2.5, color: "#1976d2",
                cursor: "pointer", textDecoration: "underline",
                border: "none", background: "none", mx: "auto",
                font: "inherit",
              }}
            >
              {t("learnMore")}
            </Typography>
          </DialogContent>
        </Dialog>

        <Dialog
          open={learnMoreOpen}
          onClose={() => setLearnMoreOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, bgcolor: "#ffffff" },
          }}
        >
          <DialogContent sx={{ py: { xs: 3, sm: 3.5 }, px: { xs: 2.5, sm: 3.5 } }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, textAlign: "center" }}>
              {t("learnMoreTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
              {t("learnMoreBody")}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
              <Button
                onClick={() => setLearnMoreOpen(false)}
                variant="contained"
                sx={{
                  borderRadius: "24px", textTransform: "none",
                  bgcolor: "#1976d2", px: 4,
                  "&:hover": { bgcolor: "#1565c0" },
                }}
              >
                {t("close")}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>

        {/* ============ 分享结果弹窗（二维码） ============ */}
        <Dialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, bgcolor: "#ffffff" },
          }}
        >
          <DialogContent sx={{ textAlign: "center", py: { xs: 3.5, sm: 4 }, px: { xs: 2.5, sm: 3 } }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {t("shareTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
              {t("shareBody", { count: results.length })}
            </Typography>

            {shareQrUrl ? (
              <Box
                component="img"
                src={shareQrUrl}
                alt="分享二維碼"
                sx={{
                  width: 220, height: 220, mx: "auto", mb: 3,
                  border: "1px solid #e0e0e0", borderRadius: 2,
                }}
              />
            ) : (
              <Typography variant="body2" color="error" sx={{ mb: 3 }}>
                {t("shareFail")}
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center" }}>
              <Button
                variant="outlined"
                onClick={handleCopyShareUrl}
                sx={{
                  borderRadius: "24px", textTransform: "none",
                  borderColor: "#1976d2", color: "#1976d2",
                  px: 3,
                  "&:hover": { bgcolor: "rgba(25,118,210,0.06)" },
                }}
              >
                {t("copyLink")}
              </Button>
              <Button
                onClick={() => setShareDialogOpen(false)}
                sx={{
                  borderRadius: "24px", textTransform: "none",
                  color: "#757575", bgcolor: "#f5f5f5",
                  px: 3,
                  "&:hover": { bgcolor: "#e0e0e0" },
                }}
              >
                {t("close")}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>

        <ImageDetailDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          image={currentImage || null}
          isMobile={isMobile}
          labels={detailLabels}
          deleting={deleting}
          scoreLabel={
            currentImage?.score != null
              ? `${Math.round(currentImage.score)}%`
              : undefined
          }
          onDownload={handleDownload}
          onPrint={handlePrint}
          onDelete={handleDeleteImage}
          getQRCodeUrl={getQRCodeUrl}
        />
      </Box>
    </ThemeProvider>
  );
}

export default FaceSearchPage;
